import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Clock, Copy, Download, HardDrive, LayoutDashboard, Loader2, Play, Radar, Search, ShieldAlert, ShoppingCart, Timer, UsersRound } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AddDomainsModal } from "../components/AddDomainsModal";
import { useToast } from "../components/overlay";
import { SourceHealth } from "../components/SourceHealth";
import { Card, EmptyState, ErrorNote, fmtBytes, fmtDuration, fmtNum, fmtRelative, Help, PageHeader, Progress, Skeleton, Stat } from "../components/ui";
import { t } from "../i18n";
import { api, ApiError } from "../services/api";
import { useCan } from "../services/auth";
import { etaSeconds } from "../services/eta";
import { useActiveJobs } from "../services/events";
import type { Attention, Domain, Overview } from "../services/types";

export const PHASE: Record<string, string> = {
  cdx: "Reading archive history", analysis: "Analyzing", plan: "Selecting captures", download: "Downloading files",
  reconstruct: "Rebuilding website", validate: "Checking quality", export: "Packaging ZIP", done: "Finishing",
};

const RISK: Record<string, string> = {
  spam: "Spam content (gambling / adult / pharma)",
  spam_urls: "Many spam URLs",
  parked: "Parked / for-sale page",
};

/** Re-renders every ``ms`` so countdowns stay current. */
function useNow(ms: number) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(id);
  }, [ms]);
  return now;
}

function NeedsAttention({ a }: { a: Attention }) {
  const now = useNow(30000);
  const qc = useQueryClient();
  const toast = useToast();
  const resume = useMutation({
    mutationFn: (jobId: number) => api.post(`/api/jobs/${jobId}/actions/resume`),
    onSuccess: () => { toast(t("Resumed from where it stopped")); qc.invalidateQueries({ queryKey: ["attention"] }); },
    onError: (e: Error) => toast(e.message, "error"),
  });
  const total = a.expiring.length + a.stuck.length + a.flagged.length;
  if (!total) {
    return (
      <div className="card-pad flex items-center gap-3">
        <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
        <div className="text-sm"><span className="font-medium">{t("Nothing needs your attention")}</span><span className="block text-slate-500">{t("No files about to be deleted, no failed jobs, no risky domains.")}</span></div>
      </div>
    );
  }
  return (
    <Card title={<span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" />{t("Needs attention")}</span>}
      description={t("Things to do now, most urgent first.")} bodyClass="p-0">
      <ul className="divide-y divide-slate-100 dark:divide-slate-800">
        {a.expiring.map((e) => {
          const left = Math.max(0, (new Date(e.files_expire_at.endsWith("Z") || /[+-]\d\d:\d\d$/.test(e.files_expire_at) ? e.files_expire_at : e.files_expire_at + "Z").getTime() - now) / 1000);
          const urgent = left < 6 * 3600;
          return (
            <li key={`e${e.job_id}`} className="flex flex-col gap-2 px-5 py-3 sm:flex-row sm:items-center">
              <Clock className={`hidden h-4 w-4 shrink-0 sm:block ${urgent ? "text-red-500" : "text-amber-500"}`} />
              <div className="min-w-0 flex-1 text-sm">
                <Link to={`/jobs/${e.job_id}`} className="font-medium hover:text-brand-600">{e.domain}</Link>
                <div className={urgent ? "text-red-600 dark:text-red-400" : "text-slate-500"}>
                  {t("Files are deleted from the server in {time}. Download the ZIP to keep the website.", { time: fmtDuration(left) })}
                </div>
              </div>
              <a className={urgent ? "btn-primary btn-sm" : "btn-secondary btn-sm"} href={`/api/jobs/${e.job_id}/export/zip`}><Download className="h-4 w-4" /> {t("Download ZIP")}</a>
            </li>
          );
        })}
        {a.stuck.map((j) => (
          <li key={`s${j.job_id}`} className="flex flex-col gap-2 px-5 py-3 sm:flex-row sm:items-center">
            <AlertTriangle className="hidden h-4 w-4 shrink-0 text-red-500 sm:block" />
            <div className="min-w-0 flex-1 text-sm">
              <Link to={`/jobs/${j.job_id}`} className="font-medium hover:text-brand-600">{j.domain ?? t("Job {id}", { id: j.job_id })}</Link>
              <div className="text-slate-500">
                {j.status === "paused" ? t("{type} is paused", { type: t(j.type === "discover" ? "Analysis" : "Recovery") }) : t("{type} failed", { type: t(j.type === "discover" ? "Analysis" : "Recovery") })}
                {j.message && <span className="line-clamp-1 text-xs"> · {j.message}</span>}
              </div>
            </div>
            <div className="flex gap-2">
              <button className="btn-primary btn-sm" disabled={resume.isPending} onClick={() => resume.mutate(j.job_id)}><Play className="h-4 w-4" /> {t("Resume")}</button>
              <Link className="btn-secondary btn-sm" to={`/jobs/${j.job_id}`}>{t("Open")}</Link>
            </div>
          </li>
        ))}
        {a.flagged.map((d) => (
          <li key={`f${d.domain_id}`} className="flex flex-col gap-2 px-5 py-3 sm:flex-row sm:items-center">
            <ShieldAlert className="hidden h-4 w-4 shrink-0 text-amber-500 sm:block" />
            <div className="min-w-0 flex-1 text-sm">
              <Link to={`/domains/${d.domain_id}`} className="font-medium hover:text-brand-600">{d.domain}</Link>
              <div className="text-slate-500">
                {d.risk ? t(RISK[d.risk]) : t("Risky period detected")}{d.periods.length > 0 && ` · ${d.periods.join(", ")}`}
                {d.recommended_flagged && <span className="text-red-600 dark:text-red-400"> · {t("every period has warnings, check the screenshots")}</span>}
              </div>
            </div>
            <Link className="btn-secondary btn-sm" to={`/domains/${d.domain_id}`}>{t("Review")}</Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

const TOOL_LABEL: Record<string, string> = { recovery: "Wayback Recovery", cloner: "Website Cloner", drops: "Domain Drop Monitor" };
const sum = (bucket: Record<string, number> | undefined, keys: string[]) => keys.reduce((n, k) => n + (bucket?.[k] ?? 0), 0);
const jobLink = (tool: string, id: number) => (tool === "cloner" ? `/tools/single-page?job=${id}` : `/jobs/${id}`);

export default function Dashboard() {
  const can = useCan();
  if (!can("dashboard.view")) return <EmptyState icon={LayoutDashboard} title={t("Choose a tool from the menu")}>{t("Your account does not include the dashboard.")}</EmptyState>;
  return <DashboardView />;
}

function DashboardView() {
  const can = useCan();
  const overview = useQuery({ queryKey: ["overview"], queryFn: () => api.get<Overview>("/api/system/overview"), refetchInterval: 60000 });
  const attention = useQuery({ queryKey: ["attention"], queryFn: () => api.get<Attention>("/api/system/attention"), refetchInterval: 30000 });
  const { jobs } = useActiveJobs();
  const qc = useQueryClient();
  const nav = useNavigate();
  const toast = useToast();
  const [name, setName] = useState("");
  const [bulk, setBulk] = useState(false);
  const add = useMutation({
    mutationFn: (n: string) => api.post<Domain>("/api/domains", { name: n, analyze: true }),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["domains"] });
      toast(t("{name} added — analysis started", { name: d.name }));
      nav(`/domains/${d.id}`);
    },
    onError: (e) => {
      const id = e instanceof ApiError ? (e.detail as any)?.domain_id : undefined;
      if (id) nav(`/domains/${id}`);
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (name.trim()) add.mutate(name.trim());
  }

  const o = overview.data;
  const a = attention.data;
  const failed = o ? sum(o.jobs.recovery, ["failed"]) + sum(o.jobs.cloner, ["failed"]) + sum(o.jobs.drops, ["failed"]) : 0;
  const activity = o?.recent_activity ?? [];
  return (
    <div className="space-y-6">
      <PageHeader title={t("Dashboard")} subtitle={t("Activity across Domain Drop Monitor, Wayback Recovery and Website Cloner.")}
        actions={o && <span className="text-xs text-slate-500" title={t("Counts are refreshed at most once a minute")}>{t("Updated {when}", { when: fmtRelative(o.generated_at) })}</span>} />

      {attention.error ? <ErrorNote error={attention.error} onRetry={() => attention.refetch()} /> : a ? <NeedsAttention a={a} /> : <div className="card-pad"><Skeleton className="h-5 w-1/3" /></div>}

      {overview.error && <ErrorNote error={overview.error} onRetry={() => overview.refetch()} />}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {o ? (
          <>
            <Stat label={t("Domains discovered")} value={fmtNum(o.drops.domains)} icon={Radar} hint={<Link to="/hunter/search" className="hover:underline">{t("Domain Drop Monitor")}</Link>} />
            <Stat label={t("Auctions closing within 24 h")} value={fmtNum(o.drops.closing_24h)} icon={Timer} tone="warning" hint={t("{n} active auctions", { n: fmtNum(o.drops.auctions) })} />
            <Stat label={t("Buy Now available")} value={fmtNum(o.drops.buy_now)} icon={ShoppingCart} hint={t("Active, recently confirmed listings")} />
            <Stat label={t("Active recovery jobs")} value={fmtNum(sum(o.jobs.recovery, ["queued", "running"]))} icon={Loader2} hint={sum(o.jobs.recovery, ["paused"]) ? t("{n} paused", { n: sum(o.jobs.recovery, ["paused"]) }) : t("Running in the background")} />
            <Stat label={t("Completed recoveries")} value={fmtNum(sum(o.jobs.recovery, ["completed"]))} icon={CheckCircle2} tone="success" />
            <Stat label={t("Failed jobs")} value={fmtNum(failed)} icon={AlertTriangle} tone={failed ? "danger" : "success"} hint={<Link to="/jobs?filter=failed" className="hover:underline">{t("All tools")}</Link>} />
            <Stat label={t("Website clones")} value={fmtNum(sum(o.jobs.cloner, ["completed", "failed", "cancelled", "queued", "running"]))} icon={Copy} hint={sum(o.jobs.cloner, ["queued", "running"]) ? t("{n} running", { n: sum(o.jobs.cloner, ["queued", "running"]) }) : undefined} />
            <Stat label={t("Storage in use")} value={fmtBytes(o.storage_bytes)} icon={HardDrive} hint={<Link to="/settings?tab=system" className="hover:underline">{t("System monitoring")}</Link>} />
            {o.users && <Stat label={t("Active users")} value={fmtNum(o.users.active)} icon={UsersRound} hint={<Link to="/users" className="hover:underline">{t("{n} accounts", { n: o.users.total })}</Link>} />}
          </>
        ) : (
          [0, 1, 2, 3, 4, 5, 6, 7].map((i) => <div key={i} className="card-pad space-y-3"><Skeleton className="h-3 w-20" /><Skeleton className="h-7 w-12" /></div>)
        )}
      </div>

      <Card title={t("Quick actions")}>
        <div className="grid gap-4 lg:grid-cols-3">
          {can("recovery.create") && <form onSubmit={submit} className="lg:col-span-2">
            <label htmlFor="quick-recover" className="text-sm font-medium">{t("Start Wayback Recovery")}</label>
            <p className="text-xs text-slate-500">{t("Enter a domain to analyze its archive history. Nothing is downloaded until you start a recovery.")}</p>
            <div className="mt-2 flex flex-col gap-2 sm:flex-row">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input id="quick-recover" className="input pl-9" placeholder="example.com" aria-label={t("Analyze a domain")} value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <button className="btn-primary" disabled={add.isPending || !name.trim()}>
                {add.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {t("Analyze Domain")}
              </button>
            </div>
            <button type="button" className="mt-2 text-xs text-brand-600 underline-offset-2 hover:underline" onClick={() => setBulk(true)}>
              {t("Have a list? Add many domains at once")}
            </button>
            {add.error && !(add.error instanceof ApiError && (add.error.detail as any)?.domain_id) && <div className="mt-2"><ErrorNote error={add.error} /></div>}
          </form>}
          <div className="flex flex-col gap-2">
            {can("drops.view") && <Link to="/hunter/search" className="btn-secondary justify-start"><Radar className="h-4 w-4" /> {t("Search domain drops")}</Link>}
            {can("cloner.create") && <Link to="/tools/single-page" className="btn-secondary justify-start"><Copy className="h-4 w-4" /> {t("Start a website clone")}</Link>}
          </div>
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title={t("Job status")} actions={<Link to="/jobs" className="text-sm text-brand-600 hover:underline">{t("All jobs")}</Link>}>
          {o && (
            <div className="mb-4 grid grid-cols-3 gap-2 text-center text-xs">
              {(["recovery", "cloner", "drops"] as const).map((tool) => (
                <div key={tool} className="rounded-lg bg-slate-50 px-2 py-2 dark:bg-slate-800/60">
                  <div className="truncate text-slate-500">{t(TOOL_LABEL[tool])}</div>
                  <div className="mt-0.5 font-semibold tabular-nums">{fmtNum(sum(o.jobs[tool], ["queued", "running"]))} <span className="font-normal text-slate-500">{t("active")}</span></div>
                </div>
              ))}
            </div>
          )}
          {jobs === null ? (
            <div className="space-y-3"><Skeleton className="h-4 w-1/2" /><Skeleton className="h-2 w-full" /></div>
          ) : jobs.length === 0 ? (
            <EmptyState title={t("Nothing running")}>{t("Jobs run on the server, so you can close this page at any time.")}</EmptyState>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {jobs.map((j) => {
                const eta = etaSeconds(j);
                return (
                  <li key={j.id} className="py-3 first:pt-0 last:pb-0">
                    <Link to={jobLink(j.type === "mirror" ? "cloner" : "recovery", j.id)} className="group block">
                      <div className="mb-2 flex items-center justify-between gap-2 text-sm">
                        <span className="truncate font-medium group-hover:text-brand-600">{j.domain_name ?? t("Job {id}", { id: j.id })}</span>
                        <span className="shrink-0 text-xs text-slate-500">
                          {t(j.type === "discover" ? "Analysis" : j.type === "recover" ? "Recovery" : j.type)} · {PHASE[j.phase ?? ""] ? t(PHASE[j.phase ?? ""]) : (j.status === "queued" ? t("Waiting") : j.phase)}
                          {eta !== null && <> · {t("about {time} left", { time: fmtDuration(eta) })}</>}
                        </span>
                      </div>
                      <Progress done={j.progress_done} total={j.progress_total} failed={j.progress_failed} />
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
        <SourceHealth sources={o?.sources} loading={overview.isLoading} />
      </div>

      <Card title={<span className="flex items-center gap-1.5">{t("Recent activity")}<Help>{t("Latest messages from all tools. Messages from the server are shown in English.")}</Help></span>}>
        {!o ? (
          <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-4 w-full" />)}</div>
        ) : activity.length === 0 ? (
          <EmptyState title={t("No activity yet")}>{t("Search domain drops or add a domain to get started.")}</EmptyState>
        ) : (
          <ul className="grid gap-x-6 gap-y-3 text-sm md:grid-cols-2">
            {activity.map((x) => (
              <li key={x.id} className="flex gap-3">
                <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${x.level === "error" ? "bg-red-500" : x.level === "warning" ? "bg-amber-500" : "bg-slate-300 dark:bg-slate-600"}`} />
                <div className="min-w-0">
                  <Link to={jobLink(x.tool, x.job_id)} className="font-medium hover:text-brand-600">{x.domain ?? t("Job {id}", { id: x.job_id })}</Link>
                  <span className="ml-2 text-xs text-slate-400">{t(TOOL_LABEL[x.tool])}</span>
                  <p className="line-clamp-2 text-slate-600 dark:text-slate-400">{x.message}</p>
                  <span className="text-xs text-slate-400">{fmtRelative(x.ts)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <AddDomainsModal open={bulk} onClose={() => setBulk(false)} />
    </div>
  );
}
